import { useEffect, useId, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

export type BurnBand = "safe" | "warn" | "over";

export interface GaugeProps {
  value: number; // 0..100+
  band: BurnBand;
  label?: string;
  size?: number;
  /** Optional ghost arc showing a projected value (0..100+). */
  projected?: number;
}

const BAND_STROKE: Record<BurnBand, string> = {
  safe: "#10b981",
  warn: "#fbbf24",
  over: "#ef4444",
};

const BAND_GLOW: Record<BurnBand, string> = {
  safe: "rgba(16, 185, 129, 0.55)",
  warn: "rgba(251, 191, 36, 0.6)",
  over: "rgba(239, 68, 68, 0.7)",
};

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = () => setReduced(m.matches);
    handler();
    m.addEventListener("change", handler);
    return () => m.removeEventListener("change", handler);
  }, []);
  return reduced;
}

/** Circular SVG HUD gauge — 270° arc with glow/bloom on the filled arc,
 * tick marks every 10%, animated needle, and a ghost arc for projection.
 * Keeps the original prop/export API; `projected` is additive and optional. */
export function Gauge({
  value,
  band,
  label,
  size = 200,
  projected,
}: GaugeProps) {
  const reduced = usePrefersReducedMotion();
  const clamped = Math.max(0, Math.min(100, value));
  const projClamped =
    projected == null ? null : Math.max(0, Math.min(120, projected));

  const stroke = 14;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const sweep = 0.75; // 270° arc
  const dashArc = circumference * sweep;

  // Animated spring for the filled dashoffset and the numeric readout.
  const mv = useMotionValue(0);
  const spring = useSpring(mv, {
    stiffness: 90,
    damping: 18,
    mass: 0.9,
  });
  useEffect(() => {
    mv.set(reduced ? clamped : clamped);
  }, [clamped, mv, reduced]);

  const readout = useTransform(spring, (v) => `${Math.round(v)}%`);
  const offset = useTransform(
    spring,
    (v) => dashArc - (dashArc * Math.max(0, Math.min(100, v))) / 100,
  );

  // Needle angle: 0% => 135°, 100% => 135° + 270° = 405° (== 45°).
  const needleAngle = useTransform(spring, (v) => 135 + (v / 100) * 270);

  // Projected ghost arc — static (no spring, it's a hint).
  const projOffset =
    projClamped == null
      ? 0
      : dashArc - (dashArc * Math.min(100, projClamped)) / 100;

  const glowId = useId();
  const pulse = band === "over" && !reduced;

  // Tick marks every 10%.
  const ticks = Array.from({ length: 11 }, (_, i) => i);

  const cx = size / 2;
  const cy = size / 2;

  return (
    <div
      className="relative flex flex-col items-center justify-center"
      style={{ width: size, height: size }}
      role="img"
      aria-label={label ? `${label} ${Math.round(clamped)}%` : `${Math.round(clamped)}%`}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: "rotate(135deg)" }}
        aria-hidden
      >
        <defs>
          <filter id={`${glowId}-glow`} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="4" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id={`${glowId}-grad`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={BAND_STROKE[band]} stopOpacity="0.9" />
            <stop offset="100%" stopColor={BAND_STROKE[band]} stopOpacity="1" />
          </linearGradient>
        </defs>

        {/* Track */}
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.05)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dashArc} ${circumference}`}
        />

        {/* Tick marks (drawn as short radial lines inside the ring). */}
        {ticks.map((i) => {
          const t = i / 10;
          const theta = t * sweep * 2 * Math.PI; // radians along the 270° arc
          const inner = radius - stroke / 2 - 2;
          const outer = radius - stroke / 2 - 8;
          const x1 = cx + inner * Math.cos(theta);
          const y1 = cy + inner * Math.sin(theta);
          const x2 = cx + outer * Math.cos(theta);
          const y2 = cy + outer * Math.sin(theta);
          const major = i % 5 === 0;
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={major ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.15)"}
              strokeWidth={major ? 2 : 1}
              strokeLinecap="round"
            />
          );
        })}

        {/* Projected ghost arc */}
        {projClamped != null && (
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke="rgba(255,255,255,0.35)"
            strokeWidth={3}
            strokeLinecap="round"
            strokeDasharray={`${dashArc} ${circumference}`}
            strokeDashoffset={projOffset}
            style={{ filter: "blur(0.3px)" }}
          />
        )}

        {/* Glow underlay (bloom). */}
        <motion.circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke={BAND_GLOW[band]}
          strokeWidth={stroke + 6}
          strokeLinecap="round"
          strokeDasharray={`${dashArc} ${circumference}`}
          strokeDashoffset={offset}
          filter={`url(#${glowId}-glow)`}
          style={{ opacity: 0.7 }}
          animate={pulse ? { opacity: [0.5, 0.9, 0.5] } : undefined}
          transition={pulse ? { duration: 1.4, repeat: Infinity, ease: "easeInOut" } : undefined}
        />

        {/* Filled arc */}
        <motion.circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke={`url(#${glowId}-grad)`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dashArc} ${circumference}`}
          strokeDashoffset={offset}
        />

        {/* Needle/marker — a small triangle+dot at the tip of the filled arc. */}
        <motion.g style={{ transformOrigin: `${cx}px ${cy}px`, rotate: needleAngle }}>
          <circle
            cx={cx + radius}
            cy={cy}
            r={6}
            fill={BAND_STROKE[band]}
            stroke="#09090f"
            strokeWidth={2}
            filter={`url(#${glowId}-glow)`}
          />
        </motion.g>
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.div className="tabular text-4xl font-bold font-display text-text-1">
          {readout}
        </motion.div>
        {label && (
          <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-text-4">
            {label}
          </div>
        )}
        {projClamped != null && (
          <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-text-3">
            proy. {Math.round(projClamped)}%
          </div>
        )}
      </div>
    </div>
  );
}
